import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import FilterTemplate from '../components/filtertemplate/FilterTemplate';
import HelpButton from '../components/shared/HelpButton';
import { useUser } from '../utils/UserContext';
import { cssUrl } from '../utils/cssVersion';
import {
  CalendarIcon, ClockIcon, UserIcon, SwapIcon, CloseIcon, DollarIcon,
} from '../components/shared/Icons';
import { fetchDeparture, downloadSouthyarra, bulkApproveDepartures, approveExtrawork, updateDeparture } from '../utils/api';
import { formatCurrency, parseCurrency } from '../utils/format';
import { CHIP_GROUP_COLORS } from '../components/shared/chipColors';
import { getBrandOrDefault } from '../data/map';



const filterStyles = cssUrl('filterTemplate.css');

// ── Row indices (get_filter_data_departure) ──
const I = {
  ID: 0, REQUEST: 1, ROOM: 2, CLEAN_DATE: 3, MATTRESS: 4, ROOM_TYPE: 5, BEDROOM: 6, BATHROOM: 7,
  KITCHEN: 8, COMMENTS: 9, STATUS: 10, EMAIL: 11, SAME_DAY: 12, SY_APPROVAL: 13, CLEANER: 15,
  ALLOCATED: 17, CLEANER_EMAIL: 18, SY_COSTS: 20, EXTRA_TIME: 22, EW_PAYMENT: 23, SNL_EW_PAYMENT: 24,
  EW_APPROVAL: 25, COMPLETED_DATE: 29,
};
// Token columns are ALWAYS the last three (cs=, sya=, ew=) — resolve from row
// length so an extra field in get_filter_data_sy can never shift them.
const tokenIndices = (row) => {
  const n = Array.isArray(row) ? row.length : 0;
  return n >= 3 ? [n - 3, n - 2, n - 1] : [30, 31, 32];
};
const BASE_SEARCH_FIELDS = [I.ROOM, I.CLEAN_DATE, I.CLEANER, I.ROOM_TYPE];

const STATUS_COMPLETED = 'Cleaning Completed';
const STATUS_PLEASE_DELETE = 'Please Delete';
const APPROVED = 'Approved';

const displayDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
};
const money = (v) => formatCurrency(parseCurrency(v));
const hasExtraWork = (item) => parseCurrency(item[I.SNL_EW_PAYMENT]) > 0;
// Extra work needs a decision while Extra Work Approval is Pending/blank (SY view 'Approve Extra Work')
const ewPending = (item) => ['', 'Pending', undefined, null].includes(item[I.EW_APPROVAL]);

// ── Chip groups — colour-coded; only ONE chip can be active at a time ──
const CHIP_GROUPS = [
  { key: 'today', color: CHIP_GROUP_COLORS.cyan, chips: [{ label: 'Today', value: 'today' }] },
  { key: 'status', color: CHIP_GROUP_COLORS.violet, chips: [
    { label: 'Pending',            value: 'cs=pending' },
    { label: 'In Progress',        value: 'cs=cleaning in progress' },
    { label: 'Completed',          value: 'cs=cleaning completed' },
    { label: 'Ready For Invoicing', value: 'cs=ready for invoicing' },
    { label: 'Invoiced',           value: 'cs=invoiced' },
    { label: 'Delete',             value: 'cs=please delete' },
    { label: 'On Hold',            value: 'cs=on hold' },
  ] },
  { key: 'approval', color: CHIP_GROUP_COLORS.rose, chips: [
    { label: 'Approved',         value: 'sya=approved' },
    { label: 'Pending Approval', value: 'sya=pending approval' },
  ] },
  { key: 'extrawork', color: CHIP_GROUP_COLORS.amber, chips: [{ label: 'Extra Work', value: 'ew=yes' }] },
];

// Same breakpoint the rest of the app uses for phone vs desktop (ProfileMap, UsersManagement)
const isPhoneViewport = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 767px)').matches;

const parseTerms = (q) => (q ? q.toLowerCase().split(',').map((t) => t.trim()).filter(Boolean) : []);

// ─────────────────────────────────────────────
// Exact, single-select chips
// ─────────────────────────────────────────────
const ExactChips = ({ chipQuery, setChipQuery, chipsOpen, setChipsOpen }) => {
  const active = parseTerms(chipQuery);

  // Desktop: open by default — FilterTemplate starts them collapsed, so ask it to open on mount.
  // Phone (< 768px, the app's mobile breakpoint): stay collapsed so the list is visible first;
  // the "▸ Filters" toggle opens them. If the template doesn't hand us a setter, just show them.
  useEffect(() => {
    if (setChipsOpen && !chipsOpen && !isPhoneViewport()) setChipsOpen(true);
  }, []); // eslint-disable-line
  const open = setChipsOpen ? chipsOpen : true;

  // One chip at a time, full stop: picking a chip replaces whatever was active,
  // clicking the active chip clears it.
  const toggle = (_group, value) => {
    setChipQuery(active.includes(value) ? '' : value);
  };

  return (
    <div className="fr-chips-wrap" style={{ padding: '0 16px 8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: open ? 8 : 0 }}>
        <button type="button" onClick={() => setChipsOpen?.(!open)}
          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.55)', fontSize: '0.75rem', cursor: 'pointer', padding: 0 }}>
          {open ? '▾ Filters' : '▸ Filters'}{active.length ? ` (${active.length})` : ''}
        </button>
        {active.length > 0 && (
          <button type="button" onClick={() => setChipQuery('')}
            style={{ background: 'none', border: '1px solid #374151', color: '#9ca3af', fontSize: '0.7rem', borderRadius: 999, padding: '2px 10px', cursor: 'pointer' }}>
            Clear All
          </button>
        )}
      </div>
      {open && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {CHIP_GROUPS.map((group, gi) => (
            <React.Fragment key={group.key}>
              {gi > 0 && <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.12)', margin: '0 2px' }} />}
              {group.chips.map(({ label, value }) => {
                const on = active.includes(value);
                return (
                  <button key={value} type="button" onClick={() => toggle(group, value)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 999,
                      fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', transition: 'all .15s ease',
                      background: on ? `${group.color}26` : 'rgba(255,255,255,0.04)',
                      color: on ? group.color : '#94a3b8',
                      border: `1px solid ${on ? group.color + '99' : group.color + '55'}`,
                      boxShadow: on ? `0 0 10px ${group.color}33` : 'none',
                    }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: on ? group.color : '#6b7280' }} />
                    {label}{on && <span style={{ opacity: 0.8 }}>×</span>}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Card
// ─────────────────────────────────────────────
const DepartureCard = ({ item, onClick }) => {
  const approved = item[I.SY_APPROVAL] === APPROVED;
  const ew = hasExtraWork(item);
  return (
    <div className="fr-card" onClick={() => onClick(item)}>
      <div className="fr-card-header">
        <div className="fr-card-dates">
          <CalendarIcon />
          <span className="fr-card-date-start">{displayDate(item[I.CLEAN_DATE])}</span>
          <span className="fr-card-date-sep">→</span>
          <span className="fr-card-date-end">{displayDate(item[I.COMPLETED_DATE]) || '—'}</span>
        </div>
        <div className="fr-card-status-badge-room" data-status={String(item[I.ROOM] || '').toLowerCase()}>
          {item[I.ROOM]}
        </div>
      </div>

      <div className="fr-card-body">
        <div className="fr-card-row">
          <SwapIcon />
          <span className="fr-card-label">Room Type:</span>
          <span className="fr-card-value">{item[I.ROOM_TYPE]}</span>
          <div className="fr-card-status-badge-room" data-status={String(item[I.MATTRESS] || '').toLowerCase()}>{item[I.MATTRESS]}</div>
        </div>
        <div className="fr-card-row">
          <ClockIcon />
          <span className="fr-card-label">Cleaning Status:</span>
          <span className="fr-card-value">{item[I.STATUS]}</span>
          <div className="fr-card-status-badge-room" data-status={approved ? 'approved' : 'pending'}>
            {item[I.SY_APPROVAL] || 'Pending Approval'}
          </div>
        </div>
        <div className="fr-card-row">
          <UserIcon />
          <span className="fr-card-label">Cleaner:</span>
          <span className="fr-card-value">{item[I.CLEANER] === 'Cleaner Name' ? 'Unassigned' : item[I.CLEANER]}</span>
          <div className="fr-card-status-badge-room" data-status={String(item[I.SAME_DAY] || '').toLowerCase()}>
            {item[I.SAME_DAY] === 'Yes' ? 'Same Day' : '-'}
          </div>
        </div>
        {ew && (
          <div className="fr-card-row">
            <DollarIcon />
            <span className="fr-card-label">Extra Work:</span>
            <span className="fr-card-value">{money(item[I.SNL_EW_PAYMENT])}</span>
            <div className="fr-card-status-badge-room"
              data-status={item[I.EW_APPROVAL] === 'Approved' ? 'approved' : item[I.EW_APPROVAL] === 'Declined' ? 'declined' : 'new'}>
              {item[I.EW_APPROVAL] || 'Needs decision'}
            </div>
          </div>
        )}
      </div>

      <div className="fr-card-footer">
        <div className="fr-card-meta">
          <span className="fr-card-meta-item">
            <DollarIcon />
            Cost: {money(item[I.SY_COSTS])} &nbsp; Requested: {displayDate(item[I.REQUEST])}
          </span>
        </div>
      </div>
      <div className="fr-card-tap-hint">Tap to view departure</div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Summary (read-only) — used by the card modal and by row-click in the popups
// ─────────────────────────────────────────────
// Tone for the Cleaning Status pill — [text/dot colour, fill]
const STATUS_TONE = {
  'pending':              ['#fbbf24', 'rgba(251,191,36,0.12)'],
  'cleaning in progress': ['#22d3ee', 'rgba(34,211,238,0.12)'],
  'cleaning completed':   ['#4ade80', 'rgba(74,222,128,0.12)'],
  'ready for invoicing':  ['#a78bfa', 'rgba(167,139,250,0.12)'],
  'invoiced':             ['#a78bfa', 'rgba(167,139,250,0.12)'],
  'please delete':        ['#f87171', 'rgba(248,113,113,0.14)'],
  'on hold':              ['#94a3b8', 'rgba(148,163,184,0.12)'],
};
const statusTone = (status) => STATUS_TONE[String(status || '').trim().toLowerCase()] || ['#e2e8f0', 'rgba(255,255,255,0.06)'];

// `cleaningStatus` overrides the row's status (the open modal keeps its own copy after a
// PATCH because selectedItem is a snapshot); `statusBusy` blurs the value while a PATCH is in
// flight; `statusAction` sits beside the "Cleaning Status" label on desktop and stacks under
// the value on phones (card modal only — the popups pass nothing).
const SummaryGrid = ({ item, cleaningStatus, statusBusy = false, statusAction = null }) => {
  const status = cleaningStatus ?? item[I.STATUS];
  const [tone, fill] = statusTone(status);
  const rows = [
    ['Room', item[I.ROOM]], ['Room Type', item[I.ROOM_TYPE]],
    ['Requested', displayDate(item[I.REQUEST])], ['Clean Date', displayDate(item[I.CLEAN_DATE])],
    ['Completed', displayDate(item[I.COMPLETED_DATE]) || '—'], ['Same Day', item[I.SAME_DAY] || '—'],
    ['Mattress', item[I.MATTRESS] || '—'], ['Bedroom', item[I.BEDROOM] || 'No'],
    ['Bathroom', item[I.BATHROOM] || 'No'], ['Kitchen', item[I.KITCHEN] || 'No'],
    ['Cleaner', item[I.CLEANER] === 'Cleaner Name' ? 'Unassigned' : item[I.CLEANER]],
    ['South Yarra Approval', item[I.SY_APPROVAL] || 'Pending Approval'],
  ];
  const extra = hasExtraWork(item)
    ? [['Extra Work', money(item[I.SNL_EW_PAYMENT])], ['Extra Work Approval', item[I.EW_APPROVAL] || 'Needs decision']]
    : [];
  const field = ([label, val]) => (
    <div key={label} className="fr-modal-field" style={{ margin: 0 }}>
      <label className="fr-modal-label">{label}</label>
      <div className="fr-modal-value">{val}</div>
    </div>
  );
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
        {rows.map(field)}
        <div className="fr-modal-field fd-status-field" style={{ margin: 0 }}>
          <label className="fr-modal-label">Cleaning Status</label>
          <div className="fd-status-value" data-busy={statusBusy ? 'true' : 'false'}>
            <span className="fd-status-pill" style={{ color: tone, background: fill, borderColor: `${tone}66` }}>{status || '—'}</span>
          </div>
          {statusAction}
        </div>
        {extra.map(field)}
      </div>
      <div className="fr-modal-field" style={{ marginTop: 12 }}>
        <label className="fr-modal-label">Comments</label>
        <div className="fr-modal-value">{item[I.COMMENTS] || 'No Comments'}</div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────
// Card modal — summary + single approve when eligible
// ─────────────────────────────────────────────
const DepartureModal = ({ item, onClose, onSubmit, isSubmitting, onToast, onSaved }) => {
  // Cleaning Status is read-only on screen; the ONLY way it changes here is the
  // "Please Delete" button below the field → PATCH → local copy updated → button gone.
  const [status, setStatus] = useState(item[I.STATUS]);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => { setStatus(item[I.STATUS]); setDeleting(false); }, [item]);

  const isDeleted = status === STATUS_PLEASE_DELETE;
  const syApproved = item[I.SY_APPROVAL] === APPROVED;
  // Once Iglu has approved, the status is frozen: no button, no PATCH from here.
  const statusLocked = isDeleted || syApproved;
  const eligible = status === STATUS_COMPLETED && !syApproved;
  const busy = isSubmitting || deleting;

  const markPleaseDelete = async () => {
    if (deleting || statusLocked) return;
    setDeleting(true);
    try {
      await updateDeparture(item[I.ID], { cleanStatus: STATUS_PLEASE_DELETE });   // throws on failure
    } catch (e) {
      setDeleting(false);
      onToast?.({ type: 'error', message: e.message || 'Could not update Cleaning Status' });
      return;
    }
    // PATCH landed: unblur, swap the read-only value, drop the button, then refresh the
    // list in the background so the card and the Delete chip pick up the new status.
    setStatus(STATUS_PLEASE_DELETE);
    setDeleting(false);
    onToast?.({ type: 'success', message: `Room ${item[I.ROOM]} marked Please Delete` });
    onSaved?.();
  };

  const deleteBtn = syApproved ? (
    <span className="fd-status-lock" title="South Yarra has approved this clean — the status can no longer change">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><rect x="4" y="11" width="16" height="10" rx="2" /><path strokeLinecap="round" d="M8 11V7a4 4 0 118 0v4" /></svg>
      Locked — approved
    </span>
  ) : isDeleted ? null : (
    <button type="button" className="fd-status-btn" onClick={markPleaseDelete} disabled={busy}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 10v7M14 10v7" /></svg>
      {deleting ? 'Assigning…' : 'Assign Please Delete'}
    </button>
  );

  return (
    <div className="fr-modal-overlay" onClick={deleting ? undefined : onClose}>
      <div className="fr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fr-modal-handle" />
        <div className="fr-modal-header">
          <h3 className="fr-modal-title">Departure — Room {item[I.ROOM]}</h3>
          <button className="fr-modal-close" disabled={deleting} onClick={deleting ? undefined : onClose}><CloseIcon /></button>
        </div>
        <div className="fr-modal-body">
          <SummaryGrid item={item} cleaningStatus={status} statusBusy={deleting} statusAction={deleteBtn} />
        </div>
        <div className="fr-modal-actions">
          <button className="fr-modal-btn fr-btn-cancel" disabled={deleting} onClick={deleting ? undefined : onClose}>Close</button>
          <button className="fr-modal-btn fr-btn-submit" disabled={!eligible || busy}
            onClick={() => onSubmit({ recordIds: [item[I.ID]] })}>
            {item[I.SY_APPROVAL] === APPROVED ? '✓ Approved'
              : isDeleted ? 'Marked Please Delete'
              : !eligible ? 'Awaiting Cleaning Completed'
              : isSubmitting ? 'Approving…' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Shared popup table — column-driven so each popup lays out its own fields
// and every header lines up over its value.
// ─────────────────────────────────────────────
const cell = { fontSize: '0.8rem', color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 };
const head = { ...cell, fontSize: '0.62rem', letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)' };

const cleanerName = (r) => (r[I.CLEANER] === 'Cleaner Name' ? 'Unassigned' : r[I.CLEANER]);

// Column sets. `w` = grid share; `render(row, ctx)` returns the cell content.
const COLS_APPROVE = [
  { key: 'room',    label: 'Room',          w: '0.9fr', render: (r, { accent }) => <span style={{ ...cell, color: accent, fontWeight: 700 }}>{r[I.ROOM]}</span> },
  { key: 'type',    label: 'Type',          w: '1.5fr', render: (r) => <span style={cell}>{r[I.ROOM_TYPE]}</span> },
  { key: 'clean',   label: 'Clean Date',    w: '0.9fr', render: (r) => <span style={cell}>{displayDate(r[I.CLEAN_DATE])}</span> },
  { key: 'done',    label: 'Complete Date', w: '0.9fr', render: (r) => <span style={cell}>{displayDate(r[I.COMPLETED_DATE]) || '—'}</span> },
  { key: 'cleaner', label: 'Cleaner',       w: '1.2fr', render: (r) => <span style={cell}>{cleanerName(r)}</span> },
  { key: 'status',  label: 'Status',        w: '1.2fr', render: (r) => <span style={cell}>{r[I.STATUS]}</span> },
];

const COLS_EXTRAWORK = [
  { key: 'room',   label: 'Room',          w: '1fr',   render: (r, { accent }) => <span style={{ ...cell, color: accent, fontWeight: 700 }}>{r[I.ROOM]}</span> },
  { key: 'clean',  label: 'Clean Date',    w: '1fr',   render: (r) => <span style={cell}>{displayDate(r[I.CLEAN_DATE])}</span> },
  { key: 'done',   label: 'Complete Date', w: '1fr',   render: (r) => <span style={cell}>{displayDate(r[I.COMPLETED_DATE]) || '—'}</span> },
  { key: 'status', label: 'Status',        w: '1.2fr', render: (r) => <span style={cell}>{r[I.STATUS]}</span> },
  { key: 'extra',  label: 'Extra Work',    w: '0.9fr', render: (r) => <span style={{ ...cell, color: CHIP_GROUP_COLORS.amber, fontWeight: 700 }}>{money(r[I.SNL_EW_PAYMENT])}</span> },
  { key: 'decide', label: 'Decision',      w: '1.4fr', render: (r, { decision }) => decision?.(r), stop: true },
];

const RowTable = ({ rows, columns, accent, onRowClick, leading = null, ctx = {} }) => {
  const template = (leading ? '28px ' : '') + columns.map((c) => c.w).join(' ');
  const rowCtx = { accent, ...ctx };
  return (
    <div style={{ maxHeight: 360, overflow: 'auto', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12 }}>
      <div style={{ minWidth: 560 }}>
        <div style={{ display: 'grid', gridTemplateColumns: template, gap: 10, padding: '8px 12px',
          borderBottom: '1px solid rgba(255,255,255,0.12)', position: 'sticky', top: 0, background: '#1b2537', zIndex: 1 }}>
          {leading && <span />}
          {columns.map((c) => <span key={c.key} style={head}>{c.label}</span>)}
        </div>
        {rows.length === 0 && (
          <div style={{ padding: 18, textAlign: 'center', color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem' }}>Nothing to show</div>
        )}
        {rows.map((r, i) => (
          <div key={r[I.ID]} className="fd-row"
            style={{ display: 'grid', gridTemplateColumns: template, gap: 10, padding: '9px 12px', alignItems: 'center',
              cursor: 'pointer', borderTop: i ? '1px solid rgba(255,255,255,0.06)' : 'none' }}
            onClick={() => onRowClick?.(r)}>
            {leading && <span onClick={(e) => e.stopPropagation()}>{leading(r)}</span>}
            {columns.map((c) => (
              <span key={c.key} style={{ minWidth: 0 }} onClick={c.stop ? (e) => e.stopPropagation() : undefined}>
                {c.render(r, rowCtx)}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const PillButton = ({ onClick, title, color, children }) => (
  <button type="button" onClick={onClick} title={title}
    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8,
      background: `${color}1a`, border: `1px solid ${color}59`, color, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', transition: 'all .15s ease' }}
    onMouseEnter={(e) => { e.currentTarget.style.background = `${color}2e`; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = `${color}1a`; }}>
    {children}
  </button>
);

// ─────────────────────────────────────────────
// BulkApprove — icon + popup (ChangeCleaner pattern)
// ─────────────────────────────────────────────
const BulkApprove = ({ items, visible, accent, onSaved, onToast }) => {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);           // row opened as a summary
  const [excluded, setExcluded] = useState(new Set());
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, approved: 0, failed: 0 });
  const [visual, setVisual] = useState(0);          // smoothed % shown on the bar
  const barRef = useRef(null);

  // Move the bar toward the end of the in-flight chunk while we wait for Airtable
  const startBar = (fromPct, toPct) => {
    clearInterval(barRef.current);
    setVisual(fromPct);
    const cap = fromPct + (toPct - fromPct) * 0.9;   // never claims the chunk is done early
    barRef.current = setInterval(() => {
      setVisual((v) => (v >= cap ? v : Math.min(cap, v + Math.max(0.5, (cap - v) * 0.08))));
    }, 80);
  };
  const stopBar = (pct) => { clearInterval(barRef.current); setVisual(pct); };

  const [shown, setShown] = useState(0);            // count shown while a chunk is in flight (ticks 1,2,3…)
  const [stage, setStage] = useState('');           // what the server is doing right now
  const countRef = useRef(null);

  // Airtable handles a chunk of 10 as one call, so tick the visible count up
  // through the chunk while we wait, then snap to the confirmed number.
  const startCount = (from, to, total) => {
    clearInterval(countRef.current);
    let n = from;
    setShown(n);
    setStage(`Reading ${to - from} record${to - from === 1 ? '' : 's'}…`);
    const step = Math.max(120, Math.min(400, 1800 / Math.max(1, to - from)));
    countRef.current = setInterval(() => {
      if (n < to - 1) {
        n += 1;
        setShown(n);
        setStage(`Approving ${n} / ${total}…`);
      } else {
        setStage(`Saving approvals & logging changes…`);
      }
    }, step);
  };
  const stopCount = (n) => { clearInterval(countRef.current); setShown(n); };
  useEffect(() => () => { clearInterval(barRef.current); clearInterval(countRef.current); }, []);

  const eligible = useMemo(
    () => items.filter((r) => r[I.STATUS] === STATUS_COMPLETED && r[I.SY_APPROVAL] !== APPROVED),
    [items]);
  const toApprove = eligible.filter((r) => !excluded.has(r[I.ID]));

  useEffect(() => { if (!open) { setSel(null); setConfirm(false); setExcluded(new Set()); } }, [open]);

  if (!visible) return null;

  // Send 10 at a time (Airtable's batch size) so the bar moves as each chunk lands
  const CHUNK = 10;
  const submit = async () => {
    if (!toApprove.length) return;
    const ids = toApprove.map((r) => r[I.ID]);
    setSaving(true);
    setShown(0);
    setStage('Starting…');
    setProgress({ done: 0, total: ids.length, approved: 0, failed: 0 });
    let approved = 0, failed = 0;
    const messages = [];
    for (let i = 0; i < ids.length; i += CHUNK) {
      const chunk = ids.slice(i, i + CHUNK);
      startBar((i / ids.length) * 100, (Math.min(i + chunk.length, ids.length) / ids.length) * 100);
      startCount(i, Math.min(i + chunk.length, ids.length), ids.length);
      try {
        const res = await bulkApproveDepartures(chunk);
        approved += res.approved || 0;
        failed += res.failed || 0;
        if (res.messages?.length) messages.push(...res.messages);
      } catch (e) {
        failed += chunk.length;
        messages.push(e.message || 'Chunk failed');
      }
      const done = Math.min(i + chunk.length, ids.length);
      setProgress({ done, total: ids.length, approved, failed });
      stopCount(done);
      stopBar((done / ids.length) * 100);
    }
    setStage(failed ? `Finished — ${approved} approved, ${failed} failed` : `Done — ${approved} / ${ids.length} approved`);
    // Buttons stay locked until the toast has been raised
    onToast?.({
      type: failed ? 'error' : 'success',
      message: `${approved} of ${ids.length} approved${failed ? ` · ${failed} failed` : ''}${messages.length ? ` — ${messages[0]}` : ''}`,
    });
    // Let the 100% bar be seen before the modal goes away, then unlock
    await new Promise((r) => setTimeout(r, 900));
    setSaving(false);
    if (approved > 0) { setOpen(false); await onSaved?.(); }
  };

  const toggleExclude = (id) => setExcluded((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <>
      <PillButton onClick={() => setOpen(true)} color={accent}
        title={`Bulk approve ${eligible.length} completed clean${eligible.length === 1 ? '' : 's'}`}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>
        Approve {eligible.length}
      </PillButton>

      {open && (
        <div className="fr-modal-overlay" onClick={() => !saving && setOpen(false)}>
          <div className="fr-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820 }}>
            <div className="fr-modal-handle" />

            <div className="fr-modal-header">
              <h3 className="fr-modal-title">{sel ? `Room ${sel[I.ROOM]}` : confirm ? 'Confirm Bulk Approval' : 'Bulk Approve — Cleaning Completed'}</h3>
              <button className="fr-modal-close" disabled={saving} onClick={() => !saving && setOpen(false)}
                style={saving ? { opacity: 0.35, cursor: 'not-allowed' } : undefined}><CloseIcon /></button>
            </div>

            <div className="fr-modal-body">
              {sel ? (
                <SummaryGrid item={sel} />
              ) : confirm ? (
                <>
                  <p style={{ fontSize: '0.9rem', color: '#f1f5f9', marginBottom: 10 }}>
                    Approve <strong style={{ color: accent }}>{toApprove.length}</strong> record{toApprove.length === 1 ? '' : 's'}?
                  </p>
                  <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', marginBottom: 12 }}>
                    Sets South Yarra Approval → <b>Approved</b> on each record.
                  </p>
                  {(saving || progress.total > 0) && (
                    <div style={{ margin: '4px 0 12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginBottom: 6 }}>
                        <span>{stage || 'Ready'}</span>
                        <span>{shown} / {progress.total} · {Math.round(visual)}%</span>
                      </div>
                      <div style={{ width: '100%', height: 8, borderRadius: 999, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                        <div style={{ width: `${visual}%`, height: '100%', borderRadius: 999,
                          transition: 'width .15s linear', background: progress.failed ? 'linear-gradient(90deg,#f59e0b,#f87171)' : 'linear-gradient(90deg,#16a34a,#4ade80)' }} />
                      </div>
                      {progress.failed > 0 && (
                        <div style={{ fontSize: '0.72rem', color: '#fca5a5', marginTop: 6 }}>{progress.failed} failed so far</div>
                      )}
                    </div>
                  )}
                  <div style={{ background: `${accent}0d`, border: `1px solid ${accent}33`, borderRadius: 10, padding: '10px 14px', maxHeight: 180, overflowY: 'auto' }}>
                    {toApprove.map((r) => (
                      <div key={r[I.ID]} style={{ fontSize: '0.78rem', color: '#e2e8f0', marginBottom: 3 }}>
                        • Room {r[I.ROOM]} — {r[I.ROOM_TYPE]} — {displayDate(r[I.CLEAN_DATE])}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', marginBottom: 10 }}>
                    {eligible.length} clean{eligible.length === 1 ? '' : 's'} marked <b>Cleaning Completed</b> awaiting approval. Untick any to hold back; tap a row for details.
                  </p>
                  <RowTable rows={eligible} columns={COLS_APPROVE} accent={accent} onRowClick={setSel}
                    leading={(r) => (
                      <input type="checkbox" checked={!excluded.has(r[I.ID])} onChange={() => toggleExclude(r[I.ID])} style={{ accentColor: accent }} />
                    )} />
                </>
              )}
            </div>

            <div className="fr-modal-actions">
              <button className="fr-modal-btn fr-btn-cancel" disabled={saving}
                style={saving ? { opacity: 0.45, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
                onClick={sel ? () => setSel(null) : confirm ? () => setConfirm(false) : () => setOpen(false)}>
                {sel || confirm ? 'Back' : 'Cancel'}
              </button>
              {!sel && (
                <button className="fr-modal-btn fr-btn-submit" disabled={saving || toApprove.length === 0}
                  style={saving ? { opacity: 0.6, cursor: 'wait', pointerEvents: 'none' } : undefined}
                  onClick={saving ? undefined : confirm ? submit : () => setConfirm(true)}>
                  {saving ? (stage || 'Working…') : confirm ? `Confirm — Approve ${toApprove.length}` : `Submit ${toApprove.length}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────
// ExtraWorkApprove — icon + popup; Approve / Decline per row, row leaves when decided
// ─────────────────────────────────────────────
const ExtraWorkApprove = ({ items, visible, accent, onSaved, onToast }) => {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [done, setDone] = useState({});            // { recordId: 'Approved' | 'Declined' } decided this session

  // Rows needing a decision when the popup opened; decided ones stay listed with their
  // decision shown as text until the popup closes (then the list refetches).
  const pending = useMemo(
    () => items.filter((r) => hasExtraWork(r) && (ewPending(r) || done[r[I.ID]])),
    [items, done]);
  const remaining = pending.filter((r) => !done[r[I.ID]]).length;

  useEffect(() => { if (!open) { setSel(null); } }, [open]);
  if (!visible) return null;

  const closeAndRefresh = async () => {
    setOpen(false);
    if (Object.keys(done).length) { await onSaved?.(); setDone({}); }
  };

  const decide = async (row, decision) => {
    setBusyId(row[I.ID]);
    try {
      await approveExtrawork(row[I.ID], decision);        // throws on any failure → red toast below
      // Both tables are updated at this point; hold a beat so the row's button state is seen,
      // then drop the row and raise the green toast.
      await new Promise((r) => setTimeout(r, 1200));
      const label = decision === 'Approve' ? 'Approved' : 'Declined';
      setDone((p) => ({ ...p, [row[I.ID]]: label }));
      onToast?.({ type: 'success', message: `Room ${row[I.ROOM]} extra work ${label.toLowerCase()}` });
    } catch (e) {
      onToast?.({ type: 'error', message: e.message || 'Update failed' });
    } finally { setBusyId(null); }
  };

  const decisionBtns = (r) => {
    const d = done[r[I.ID]];
    if (d) {
      const ok = d === 'Approved';
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', fontWeight: 700,
          color: ok ? '#4ade80' : '#fca5a5' }}>
          {ok ? '✓' : '✕'} {d}
        </span>
      );
    }
    return (
    <span style={{ display: 'flex', gap: 6 }}>
      <button type="button" disabled={busyId === r[I.ID]} onClick={() => decide(r, 'Approve')}
        style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid rgba(34,197,94,0.5)', background: 'rgba(34,197,94,0.12)', color: '#4ade80', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
        {busyId === r[I.ID] ? 'Saving…' : 'Approve'}
      </button>
      <button type="button" disabled={busyId === r[I.ID]} onClick={() => decide(r, 'Decline')}
        style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid rgba(248,113,113,0.5)', background: 'rgba(248,113,113,0.12)', color: '#fca5a5', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
        Decline
      </button>
    </span>
    );
  };

  return (
    <>
      <PillButton onClick={() => setOpen(true)} color={CHIP_GROUP_COLORS.amber}
        title={`${remaining} extra work item${remaining === 1 ? '' : 's'} to decide`}>
        <DollarIcon /> Extra Work {remaining}
      </PillButton>

      {open && (
        <div className="fr-modal-overlay" onClick={busyId ? undefined : closeAndRefresh}>
          <div className="fr-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900 }}>
            <div className="fr-modal-handle" />
            <div className="fr-modal-header">
              <h3 className="fr-modal-title">{sel ? `Room ${sel[I.ROOM]}` : 'Extra Work — Approve or Decline'}</h3>
              <button className="fr-modal-close" disabled={!!busyId} onClick={busyId ? undefined : closeAndRefresh}><CloseIcon /></button>
            </div>
            <div className="fr-modal-body">
              {sel ? <SummaryGrid item={sel} /> : (
                <>
                  <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', marginBottom: 10 }}>
                    Rooms with extra work charged. Decide each row; the list refreshes when you close. Tap a row for details.
                  </p>
                  <RowTable rows={pending} columns={COLS_EXTRAWORK} accent={accent} onRowClick={setSel}
                    ctx={{ decision: decisionBtns }} />
                </>
              )}
            </div>
            <div className="fr-modal-actions">
              <button className="fr-modal-btn fr-btn-cancel" disabled={!!busyId}
                onClick={sel ? () => setSel(null) : closeAndRefresh}>
                {sel ? 'Back' : remaining === 0 && pending.length > 0 ? 'Done — Close & Refresh' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────
const FilterDeparture = ({ showHeader = false }) => {
  const { user } = useUser();
  const accent = getBrandOrDefault(user).accent;

  const [allItems, setAllItems] = useState([]);
  const [chipQuery, setChipQuery] = useState('');
  const [localToast, setLocalToast] = useState(null);
  const refetchRef = useRef(null);

  const handleItemsChange = useCallback((items) => setAllItems(Array.isArray(items) ? items : []), []);
  const handleChipQueryChange = useCallback((q) => setChipQuery(q || ''), []);

  useEffect(() => {
    if (!localToast) return;
    const t = setTimeout(() => setLocalToast(null), 3500);
    return () => clearTimeout(t);
  }, [localToast]);

  // Search + chip fields: the four text columns plus wherever the tokens landed
  const searchFields = useMemo(
    () => [...BASE_SEARCH_FIELDS, ...tokenIndices(allItems[0])],
    [allItems]);

  const terms = parseTerms(chipQuery);
  const showBulk = terms.includes('cs=cleaning completed');
  const showExtra = terms.includes('ew=yes');

  // Card modal single approve goes through the same bulk endpoint.
  // useFilterData.handleSubmit decides by result.status ('success' | 'partial' | else),
  // so translate the bulk endpoint's { success, approved, failed } into that shape —
  // on 'success' the template closes the modal, refetches the month and shows the green toast.
  const handleSubmit = async ({ recordIds }) => {
    const res = await bulkApproveDepartures(recordIds);
    const approved = res.approved || 0;
    const failed = res.failed || 0;
    if (approved > 0 && failed === 0) {
      // Hold the modal (button shows "Approving…") for a beat so the user sees it
      // land, then let the template close, toast and refetch the month.
      await new Promise((r) => setTimeout(r, 2500));
      return { status: 'success', message: res.message || 'Approved' };
    }
    if (approved > 0) {
      return { status: 'partial', message: res.message || `${approved} approved, ${failed} failed` };
    }
    return { status: 'error', error: res.message || 'Approve failed' };
  };

  return (
    <>
      <style>{`
        .fd-row:hover{background:${accent}12}
        .fd-status-field{grid-column:1 / -1;display:grid;grid-template-columns:1fr auto;grid-template-areas:"label btn" "value value";align-items:center;column-gap:16px;row-gap:10px}
        .fd-status-field .fr-modal-label{grid-area:label;margin:0}
        .fd-status-value{grid-area:value;display:flex;align-items:center;min-height:34px;transition:filter .2s ease,opacity .2s ease}
        .fd-status-value[data-busy="true"]{filter:blur(3px);opacity:.55;pointer-events:none}
        .fd-status-pill{display:inline-flex;align-items:center;gap:9px;padding:7px 16px;border-radius:999px;border:1px solid;font-size:.92rem;font-weight:700;letter-spacing:.01em}
        .fd-status-pill::before{content:'';width:8px;height:8px;border-radius:50%;background:currentColor;box-shadow:0 0 8px currentColor}
        .fd-status-btn{grid-area:btn;display:inline-flex;align-items:center;justify-content:center;gap:8px;padding:8px 14px;border-radius:10px;border:1px solid rgba(248,113,113,.5);background:linear-gradient(135deg,rgba(248,113,113,.18),rgba(190,18,60,.22));color:#fecaca;font-size:.76rem;font-weight:700;letter-spacing:.02em;white-space:nowrap;cursor:pointer;transition:all .15s ease;box-shadow:0 2px 12px rgba(248,113,113,.12)}
        .fd-status-btn:hover:not(:disabled){background:linear-gradient(135deg,rgba(248,113,113,.32),rgba(190,18,60,.36));border-color:rgba(248,113,113,.85);transform:translateY(-1px);box-shadow:0 4px 16px rgba(248,113,113,.22)}
        .fd-status-btn:disabled{opacity:.55;cursor:wait;transform:none}
        .fd-status-lock{grid-area:btn;display:inline-flex;align-items:center;gap:6px;font-size:.72rem;color:rgba(255,255,255,.45);white-space:nowrap}
        @media (max-width:559px){
          .fd-status-field{grid-template-columns:1fr;grid-template-areas:"label" "value" "btn"}
          .fd-status-btn{width:100%}
        }
      `}</style>
      {localToast && (
        <div className={`ob-toast ${localToast.type === 'success' ? 'ob-toast-success' : 'ob-toast-error'}`}
          style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
          {localToast.message}
        </div>
      )}

      <FilterTemplate
        title="Departures"
        helpButton={<HelpButton helpData={[{ chip: 'departure_filter', urlFilter: '/static/pdffilters/DepartureIgluFilter.pdf' }]} />}
        subtitle="Approve completed cleans"
        searchPlaceholder="Search room, cleaner, status..."
        stylesheetHref={filterStyles}
        fetchData={fetchDeparture}
        dataKey="departure"
        searchFields={searchFields}
        onSubmit={handleSubmit}
        loadingMessage="Loading departures..."
        showNavigation={true}
        showHeader={showHeader}
        chipLogic="and"
        todayFilterIndex={I.CLEAN_DATE}
        onDownload={downloadSouthyarra}
        onChipQueryChange={handleChipQueryChange}
        onItemsChange={handleItemsChange}
        onRefetchRef={refetchRef}
        renderExtraActions={() => (
          <>
            <BulkApprove items={allItems} visible={showBulk} accent={accent}
              onSaved={() => refetchRef.current?.()} onToast={setLocalToast} />
            <ExtraWorkApprove items={allItems} visible={showExtra} accent={accent}
              onSaved={() => refetchRef.current?.()} onToast={setLocalToast} />
          </>
        )}
        renderChips={(cq, setCq, chipsOpen, setChipsOpen) => (
          <ExactChips chipQuery={cq} setChipQuery={setCq} chipsOpen={chipsOpen} setChipsOpen={setChipsOpen} />
        )}
        renderCard={(item, onClick) => <DepartureCard item={item} onClick={onClick} />}
        renderModal={(item, onClose, onSubmit, isSubmitting) => (
          <DepartureModal item={item} onClose={onClose} onSubmit={onSubmit} isSubmitting={isSubmitting}
            onToast={setLocalToast} onSaved={() => refetchRef.current?.()} />
        )}
      />
    </>
  );
};

export default FilterDeparture;